import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { AnalyticsEvents, type AppReloadEventProperties } from '@/lib/analytics-events'
import { captureEvent, captureException } from '@/lib/posthog'

const LAST_BUNDLE_VERSION_KEY = 'zrm_last_bundle_version'

export async function initOTAUpdates(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  await CapacitorUpdater.notifyAppReady()
}

export async function registerOtaLifecycleListener(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  // A bundle downloaded this session and waiting to be applied on background.
  let pendingBundleId: string | null = null
  // The version already downloaded + staged, so repeated foregrounds (e.g. a
  // push notification re-activating the app) don't re-download the same bundle.
  let stagedVersion: string | null = null

  try {
    await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        let latest
        try {
          latest = await CapacitorUpdater.getLatest()
        } catch {
          // getLatest throws "no_new_version_available" when already current, and
          // also when the device is offline — both are expected, high-frequency
          // paths, so they stay silent (retried on the next foreground).
          return
        }
        if (!latest.url || latest.version === stagedVersion) return

        try {
          const bundle = await CapacitorUpdater.download({
            url: latest.url,
            version: latest.version,
          })
          pendingBundleId = bundle.id
          stagedVersion = latest.version
          // Stage the bundle natively so the update still applies if the user
          // force-quits instead of backgrounding — in which case the set() path
          // below never runs. next() activates the bundle on the next background,
          // kill + relaunch, or reload(), and survives the JS context being torn
          // down. Best-effort: the background set() remains the primary apply path.
          try {
            await CapacitorUpdater.next({ id: bundle.id })
          } catch {
            // Staging is a belt-and-suspenders extra; ignore if unsupported/failed.
          }
        } catch (err) {
          // A new version exists but we couldn't fetch/unpack it (disk full,
          // corrupt bundle, connection dropped mid-download). Surface it so these
          // failures are visible in analytics instead of failing invisibly the
          // way this used to — this is how "the update never arrived" goes unseen.
          try {
            captureException(err instanceof Error ? err : new Error(String(err)), {
              source: 'ota_download',
            })
          } catch {
            // captureException failed — nothing more we can do.
          }
        }
        return
      }

      if (pendingBundleId) {
        const idToApply = pendingBundleId
        pendingBundleId = null
        try {
          await CapacitorUpdater.set({ id: idToApply })
        } catch (err) {
          try {
            captureException(err instanceof Error ? err : new Error(String(err)), {
              source: 'ota_background_apply',
            })
          } catch {
            // captureException failed — nothing more we can do without leaking an unhandled rejection.
          }
        }
      }
    })
  } catch {
    // Capacitor bridge unavailable — OTA background-apply disabled for this session.
  }
}

export async function captureAppReloadTelemetry(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const current = await CapacitorUpdater.current()
    const bundleVersion = current.bundle.version || null
    if (!bundleVersion) return

    let previousBundleVersion: string | null = null
    try {
      previousBundleVersion = window.localStorage.getItem(LAST_BUNDLE_VERSION_KEY)
    } catch {
      // localStorage unavailable; treat as first boot.
    }

    const cause: AppReloadEventProperties['cause'] =
      previousBundleVersion && previousBundleVersion !== bundleVersion
        ? 'capgo_update'
        : 'cold_start'

    captureEvent(AnalyticsEvents.APP_RELOAD, {
      cause,
      bundle_version: bundleVersion,
      previous_bundle_version: previousBundleVersion,
    } satisfies AppReloadEventProperties)

    try {
      if (bundleVersion) window.localStorage.setItem(LAST_BUNDLE_VERSION_KEY, bundleVersion)
    } catch {
      // localStorage unavailable — the event still fired, which is what matters.
    }
  } catch {
    // Plugin not ready or platform weirdness — silently skip telemetry.
  }
}
